// Style Imports
import Styles from './AssessmentReportCard.module.scss';

// React Imports 
import { useState, useRef } from 'react';

// Component Imports
import ReactPlayer from 'react-player';
import { Transcript } from 'components/global/LMS/Transcript/Transcript';

// MUI Imports
import {
    Container,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Divider,
    Tooltip,
    Alert,
    Button,
    Box,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    IconButton
} from '@mui/material';

// Icon Imports
import {
    Info,
    CancelOutlined,
    CheckCircleOutline,
    Close,
    Description,
    Assignment,
    AccessTime,
    Insights,
    AssignmentTurnedIn,
    AutoAwesomeRounded,
} from '@mui/icons-material';

// Chart Imports
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ReferenceLine,
    Cell,
    ResponsiveContainer,
    Tooltip as RechartsTooltip
} from 'recharts';


export default function AssessmentReportCard({ reportDetails, handleReporPreviewClose, error, setError }) {
    // State for managing which feedback (case study) is currently being viewed
    const [selectedFeedbackIndex, setSelectedFeedbackIndex] = useState(0);
    // State for managing which tab is active (Results or Response)
    const [activeTab, setActiveTab] = useState(0);
    const playerRef = useRef(null);

    const handleFeedbackChange = (event) => {
        setSelectedFeedbackIndex(event.target.value);
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    if (error) {
        return (
            <Container className={Styles.errorContainer}>
                <Alert severity="error" className={Styles.errorMessage}>{error}</Alert>
                <Button
                    variant="contained"
                    startIcon={<Close />}
                    onClick={handleReporPreviewClose}
                    className={Styles.backButton}
                >
                    Close
                </Button>
            </Container>
        );
    }

    // Check if ai_feedback is an array, if not, convert it to an array for consistency
    const feedbackList = Array.isArray(reportDetails?.results)
        ? reportDetails.results
        : reportDetails?.results ? [reportDetails.results] : [];

    // Get the currently selected feedback
    const currentFeedback = feedbackList[selectedFeedbackIndex] || {};
    const currentUserResponse = feedbackList[selectedFeedbackIndex]?.user_response
    const notAttempted = currentUserResponse === null || currentUserResponse === undefined || currentUserResponse === '';
    const userVideo = feedbackList[selectedFeedbackIndex]?.user_video;
    const transcript = feedbackList[selectedFeedbackIndex]?.transcript_with_timestamp

    // Calculate average score across all case studies
    const averageScore = feedbackList.length > 0
        ? (reportDetails?.score?.average_feedback_percent || 0)
        : 0;

    // Calculate average AI similarity percentage across all case studies
    const averageAiSimilarity = feedbackList.length > 0
        ? (reportDetails?.score?.average_ai_similarity_percent || 0)
        : 0;

    // Count number of attempted case studies
    const attemptedCaseStudies = feedbackList.filter(feedback =>
        feedback?.user_response || feedback?.user_video
    ).length;

    // Total number of case studies
    const totalCaseStudies = feedbackList.length;

    // Format date and time helpers
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    // Calculate time taken in minutes
    const calculateTimeTaken = (startTime, endTime) => {
        if (!startTime || !endTime) return '-';

        const start = new Date(startTime);
        const end = new Date(endTime);

        // Calculate difference in milliseconds
        const diffMs = end - start;

        // Convert to minutes
        const minutes = Math.floor(diffMs / 60000);
        const seconds = Math.floor((diffMs % 60000) / 1000);

        return `${minutes} min ${seconds} sec`;
    };

    const getScoreColor = (score) => {
        if (score >= 80) return Styles.excellentScore;
        if (score >= 60) return Styles.goodScore;
        if (score >= 40) return Styles.averageScore;
        return Styles.poorScore;
    };

    const getAiUtilizationScore = (score) => {
        if (score >= 80) return Styles.poorScore;
        if (score >= 60) return Styles.averageScore;
        if (score >= 40) return Styles.goodScore;
        return Styles.excellentScore;
    };

    const getBarColor = (score) => {
        if (score >= 80) {
            return '#2e7d32';  // Green
        } else if (score >= 60) {
            return '#1976d2';  // Blue
        } else if (score >= 40) {
            return '#ff9800';  // Orange
        } else {
            return '#d32f2f';  // Red
        }
    };

    const getClassification = (classification) => {
        switch (classification) {
            case 'human_written':
                return { text: 'Human Written', type: 'success' };
            case 'ai_generated':
                return { text: 'AI Generated', type: 'warning' };
            case 'uncertain':
                return { text: 'Uncertain', type: 'info' };
            default:
                return { text: 'Uncertain', type: 'info' };
        }
    }

    return (
        <div className={`${Styles.resultsContainer}`}>
            <div className={`${Styles.headerButtonsSection} ${Styles.hideForPdf}`}>
                <IconButton
                    onClick={handleReporPreviewClose}
                >
                    <Close />
                </IconButton>
            </div>
            <div className={Styles.headerSection}>
                <Typography variant="h4" className={Styles.pageTitle}>
                    Assessment Evaluation Results
                </Typography>
            </div>

            <Divider className={Styles.divider} />

            {/* User info section */}
            <div className={Styles.userInfoSection}>
                <Paper elevation={2} className={Styles.userInfoCard}>
                    <Typography variant="h5" className={Styles.sectionTitle}>
                        Student Information
                    </Typography>
                    <div className={Styles.userInfoGrid}>
                        <div className={Styles.userInfoItem}>
                            <Typography variant="subtitle2" color="textSecondary" className={Styles.userInfoHeading}>
                                Roll Number
                            </Typography>
                            <Typography variant="body1" className={Styles.userInfoValue}>
                                {reportDetails?.roll_number || '-'}
                            </Typography>
                        </div>
                        <div className={Styles.userInfoItem}>
                            <Typography variant="subtitle2" color="textSecondary" className={Styles.userInfoHeading}>
                                Name
                            </Typography>
                            <Typography variant="body1" className={Styles.userInfoValue}>
                                {reportDetails?.name || ''}
                            </Typography>
                        </div>
                        <div className={Styles.userInfoItem}>
                            <Typography variant="subtitle2" color="textSecondary" className={Styles.userInfoHeading}>
                                Email
                            </Typography>
                            <Typography variant="body1" className={Styles.userInfoValue}>
                                {reportDetails?.email || '-'}
                            </Typography>
                        </div>
                        <div className={Styles.userInfoItem}>
                            <Typography variant="subtitle2" color="textSecondary" className={Styles.userInfoHeading}>
                                Institute Name
                            </Typography>
                            <Typography variant="body1" className={Styles.userInfoValue}>
                                {reportDetails?.institution_name || '-'}
                            </Typography>
                        </div>
                        <div className={Styles.userInfoItem}>
                            <Typography variant="subtitle2" color="textSecondary" className={Styles.userInfoHeading}>
                                Class
                            </Typography>
                            <Typography variant="body1" className={Styles.userInfoValue}>
                                {reportDetails?.student_class || '-'}
                            </Typography>
                        </div>
                        <div className={Styles.userInfoItem}>
                            <Typography variant="subtitle2" color="textSecondary" className={Styles.userInfoHeading}>
                                Division
                            </Typography>
                            <Typography variant="body1" className={Styles.userInfoValue}>
                                {reportDetails?.student_division || '-'}
                            </Typography>
                        </div>
                    </div>
                </Paper>
            </div>

            {/* Time and Overall Metrics Section */}
            <div className={Styles.metricsSection}>
                <Paper elevation={2} className={Styles.metricsPaper}>
                    <Typography variant="h5" className={Styles.sectionTitle}>
                        Assessment Overview
                    </Typography>
                    <Grid container spacing={2} className={Styles.metricsGrid}>
                        <Grid item xs={12} sm={6} md={3}>
                            <div className={Styles.metricItem}>
                                <Typography variant="subtitle2" color="textSecondary" className={Styles.metricLabel}>
                                    <AccessTime fontSize="small" className={Styles.metricIcon} /> Start Time
                                </Typography>
                                <Typography variant="body1" className={Styles.metricValue}>
                                    {formatDate(reportDetails?.start_time) || '-'}
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <div className={Styles.metricItem}>
                                <Typography variant="subtitle2" color="textSecondary" className={Styles.metricLabel}>
                                    <AccessTime fontSize="small" className={Styles.metricIcon} /> End Time
                                </Typography>
                                <Typography variant="body1" className={Styles.metricValue}>
                                    {formatDate(reportDetails?.end_time) || '-'}
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <div className={Styles.metricItem}>
                                <Typography variant="subtitle2" color="textSecondary" className={Styles.metricLabel}>
                                    <AccessTime fontSize="small" className={Styles.metricIcon} /> Time Taken
                                </Typography>
                                <Typography variant="body1" className={Styles.metricValue}>
                                    {calculateTimeTaken(reportDetails?.start_time, reportDetails?.end_time)}
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <div className={Styles.metricItem}>
                                <Typography variant="subtitle2" color="textSecondary" className={Styles.metricLabel}>
                                    <Assignment fontSize="small" className={Styles.metricIcon} /> Case Studies
                                </Typography>
                                <Typography variant="body1" className={Styles.metricValue}>
                                    <Tooltip title="Attempted / Total Case Studies">
                                        <span>{attemptedCaseStudies} / {totalCaseStudies}</span>
                                    </Tooltip>
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <div className={`${Styles.metricItem} ${Styles.averageScoreItem}`}>
                                <Typography variant="subtitle2" color="textSecondary" className={Styles.metricLabel}>
                                    <AssignmentTurnedIn fontSize="small" className={Styles.metricIcon} />  Minimum Required
                                </Typography>
                                <Typography
                                    variant="body1"
                                    className={`${Styles.metricValue}`}
                                >
                                    <Tooltip title="Minimum Case Studies to Attempt">
                                        <span>{totalCaseStudies}</span>
                                    </Tooltip>
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <div className={`${Styles.metricItem} ${Styles.averageScoreItem}`}>
                                <Typography variant="subtitle2" color="textSecondary" className={Styles.metricLabel}>
                                    Max Total Score
                                </Typography>
                                <Typography
                                    variant="body1"
                                    className={`${Styles.metricValue}`}
                                >
                                    <Tooltip title="Secured Score / Max Total Score">
                                        <span>{reportDetails?.score?.total_feedback_percent} / {feedbackList.length * 100}</span>
                                    </Tooltip>
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <div className={`${Styles.metricItem} ${Styles.averageScoreItem}`}>
                                <Typography variant="subtitle2" color="textSecondary" className={Styles.metricLabel}>
                                    Average Score
                                </Typography>
                                <Typography
                                    variant="body1"
                                    className={`${Styles.metricValue} ${getScoreColor(averageScore)}`}
                                >
                                    {parseFloat(averageScore).toFixed(1)}%
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <div className={`${Styles.metricItem} ${Styles.averageAiItem}`}>
                                <Typography variant="subtitle2" color="textSecondary" className={Styles.metricLabel}>
                                    Average AI Similarity
                                </Typography>
                                <Typography
                                    variant="body1"
                                    className={`${Styles.metricValue} ${getAiUtilizationScore(averageAiSimilarity)}`}
                                >
                                    {parseFloat(averageAiSimilarity).toFixed(1)}%
                                </Typography>
                            </div>
                        </Grid>
                    </Grid>
                    {attemptedCaseStudies < totalCaseStudies && (
                        <Alert severity="info" className={Styles.attemptAlert}>
                            <Typography variant="body2">
                                {attemptedCaseStudies === 0 ?
                                    "No case studies have been attempted yet." :
                                    `${totalCaseStudies - attemptedCaseStudies} case ${totalCaseStudies - attemptedCaseStudies === 1 ? 'study has' : 'studies have'} not been attempted.`
                                }
                            </Typography>
                        </Alert>
                    )}
                </Paper>
            </div>

            {/* Case Study Dropdown - Replace the tabs with a dropdown */}
            {feedbackList.length > 0 && (
                <div className={Styles.caseStudySelectContainer}>
                    <Paper elevation={2} className={Styles.selectPaper}>
                        <FormControl fullWidth className={Styles.caseStudySelect}>
                            <InputLabel id="case-study-select-label">Select Case Study</InputLabel>
                            <Select
                                labelId="case-study-select-label"
                                id="case-study-select"
                                value={selectedFeedbackIndex}
                                label="Select Case Study"
                                onChange={handleFeedbackChange}
                            >
                                {feedbackList.map((feedback, index) => (
                                    <MenuItem key={index} value={index}>
                                        {feedback.name || `Case Study ${index + 1}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Paper>
                </div>
            )}

            {/* Tab Navigation: Results and Response */}
            <Box sx={{ width: '100%', mb: 2, mt: 2 }}>
                <Paper elevation={2}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        aria-label="report tabs"
                        className={Styles.reportTabs}
                        centered
                    >
                        <Tab fullwidth icon={<Insights />} iconPosition="start" label="Results" />
                        <Tab fullwidth icon={<Description />} iconPosition="start" label="Response" />
                    </Tabs>
                </Paper>
            </Box>

            {/* Results Tab Content */}
            {activeTab === 0 && (
                <>
                    {((!currentFeedback || notAttempted) && !userVideo) ? (
                        <div className={Styles.noFeedbackContainer}>
                            <Paper elevation={2} className={Styles.noFeedbackPaper}>
                                <Info className={Styles.infoIcon} />
                                <Typography variant="h6" className={Styles.noFeedbackTitle}>
                                    {notAttempted ? "Case Study Not Attempted" : "No Feedback Available"}
                                </Typography>
                                <Typography variant="body1" className={Styles.noFeedbackText}>
                                    {notAttempted
                                        ? "User has not submitted any response to this case study."
                                        : "There is no feedback data available for this item yet."
                                    }
                                </Typography>
                            </Paper>
                        </div>
                    ) : (
                        <div className={Styles.resultsSection}>

                            {/* Classification Indicator */}
                            <Alert severity={getClassification(currentFeedback.classification).type} className={Styles.classificationAlert}>
                                <Typography variant="body1">
                                    Classification: {getClassification(currentFeedback.classification).text}
                                </Typography>
                            </Alert>
                            {/* Score Section */}
                            <div className={Styles.scoreSection}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
                                    {/* Overall Score */}
                                    <div className={Styles.progressWrapper}>
                                        <Tooltip title={`Your final score: ${currentFeedback?.feedback_percent || 0}%`}>
                                            <CircularProgress
                                                variant="determinate"
                                                value={currentFeedback?.feedback_percent}
                                                size={150}
                                                thickness={4}
                                                className={`${Styles.progressCircle} ${getScoreColor(currentFeedback?.feedback_percent)}`}
                                            />
                                        </Tooltip>
                                        <div className={Styles.scoreContent}>
                                            <Typography variant="h4" className={Styles.scoreText}>
                                                {currentFeedback?.feedback_percent ? parseFloat(currentFeedback.feedback_percent).toFixed(1) : '0'}%
                                            </Typography>
                                            <Typography variant="subtitle2" className={Styles.scoreLabel}>
                                                Overall Score
                                            </Typography>
                                        </div>
                                    </div>
                                    {/* AI Utilization Score */}
                                    <div className={Styles.progressWrapper}>
                                        <Tooltip title={`AI Similarity percentage: ${currentFeedback?.ai_similarity_percent || 0}%`}>
                                            <CircularProgress
                                                variant="determinate"
                                                value={currentFeedback?.ai_similarity_percent}
                                                size={150}
                                                thickness={4}
                                                className={`${Styles.progressCircle} ${getAiUtilizationScore(currentFeedback?.ai_similarity_percent)}`}
                                            />
                                        </Tooltip>
                                        <div className={Styles.scoreContent}>
                                            <Typography variant="h4" className={Styles.scoreText}>
                                                {currentFeedback?.ai_similarity_percent ? parseFloat(currentFeedback.ai_similarity_percent).toFixed(1) : '0'}%
                                            </Typography>
                                            <Typography variant="subtitle2" className={Styles.scoreLabel}>
                                                AI Similarity
                                            </Typography>
                                        </div>
                                    </div>
                                </div>
                                {/* Spider Chart */}
                                {currentFeedback?.criteria_list?.length >= 0 && (
                                    <div className={Styles.barChartContainer}>
                                        <Typography variant="h6" className={Styles.chartTitle}>
                                            Performance by Criteria
                                        </Typography>
                                        <ResponsiveContainer width={500} height={250}>
                                            <BarChart
                                                data={currentFeedback.criteria_list}
                                                layout="vertical"
                                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                                                <XAxis
                                                    type="number"
                                                    domain={[0, 100]}
                                                    tick={{ fill: '#666', fontSize: 10 }}
                                                    tickFormatter={(value) => `${value}%`}
                                                />
                                                <YAxis
                                                    type="category"
                                                    dataKey="criteria"
                                                    tick={{ fill: '#666', fontSize: 12 }}
                                                    width={90}
                                                />
                                                <RechartsTooltip formatter={(value) => `${value}%`} />
                                                <Bar
                                                    dataKey="score"
                                                    name="Criteria Score"
                                                    radius={[0, 4, 4, 0]}
                                                >
                                                    {/* Use Cell components to dynamically color each bar based on its score */}
                                                    {currentFeedback.criteria_list.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={getBarColor(entry.score)} />
                                                    ))}
                                                </Bar>
                                                <ReferenceLine x={40} stroke="#ff9800" strokeDasharray="3 3" />
                                                <ReferenceLine x={60} stroke="#ff9800" strokeDasharray="3 3" />
                                                <ReferenceLine x={80} stroke="#ff9800" strokeDasharray="3 3" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </div>

                            {/* Evaluation Section */}
                            <div className={Styles.evaluationSection}>
                                <Paper elevation={2} className={Styles.evaluationPaper}>
                                    <Typography variant="h5" className={Styles.sectionTitle}>
                                        Overall Evaluation
                                    </Typography>
                                    <Typography variant="body1" className={Styles.evaluationText}>
                                        {currentFeedback?.evaluation || "No evaluation data available at this time."}
                                    </Typography>
                                </Paper>
                            </div>

                            {/* Strengths & Weaknesses Section - Side by Side */}
                            <div className={Styles.feedbackSections}>
                                {/* Strengths Section */}
                                <div className={Styles.strengthsSection}>
                                    <Paper elevation={2} className={Styles.strengthBox}>
                                        <Typography variant="h5" className={Styles.strengthTitle}>
                                            Strengths
                                        </Typography>
                                        {currentFeedback?.strengths && currentFeedback.strengths.length > 0 ? (
                                            <List className={Styles.feedbackList}>
                                                {currentFeedback.strengths.map((strength, index) => (
                                                    <ListItem key={index} alignItems="flex-start">
                                                        <ListItemIcon>
                                                            <CheckCircleOutline className={Styles.strengthIcon} />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={strength}
                                                            className={Styles.feedbackItem}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        ) : (
                                            <Typography variant="body1" className={Styles.noDataMessage}>
                                                No strengths identified yet.
                                            </Typography>
                                        )}
                                    </Paper>
                                </div>

                                {/* Weaknesses Section */}
                                <div className={Styles.weaknessesSection}>
                                    <Paper elevation={2} className={Styles.weaknessBox}>
                                        <Typography variant="h5" className={Styles.weaknessTitle}>
                                            Areas for Improvement
                                        </Typography>
                                        {currentFeedback?.weakness && currentFeedback.weakness.length > 0 ? (
                                            <List className={Styles.feedbackList}>
                                                {currentFeedback.weakness.map((weakness, index) => (
                                                    <ListItem key={index} alignItems="flex-start">
                                                        <ListItemIcon>
                                                            <CancelOutlined className={Styles.weaknessIcon} />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={weakness}
                                                            className={Styles.feedbackItem}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        ) : (
                                            <Typography variant="body1" className={Styles.noDataMessage}>
                                                No improvement areas identified yet.
                                            </Typography>
                                        )}
                                    </Paper>
                                </div>
                            </div>

                            {/* Detailed Feedback Section */}
                            <div className={Styles.detailedFeedbackSection}>
                                <Paper elevation={2} className={Styles.feedbackBox}>
                                    <div className={Styles.feedbackHeader}>
                                        <Typography variant="h5" className={Styles.sectionTitle}>
                                            Detailed Feedback
                                        </Typography>
                                    </div>
                                    <Typography variant="body1" className={Styles.detailedFeedbackText}>
                                        {currentFeedback?.feedback || "No detailed feedback available at this time."}
                                    </Typography>
                                </Paper>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Response Tab Content */}
            {
                activeTab === 1 && (
                    <>
                        {((!currentFeedback || notAttempted) && !userVideo) ? (
                            <div className={Styles.noFeedbackContainer}>
                                <Paper elevation={2} className={Styles.noFeedbackPaper}>
                                    <Info className={Styles.infoIcon} />
                                    <Typography variant="h6" className={Styles.noFeedbackTitle}>
                                        {notAttempted ? "Case Study Not Attempted" : "No Feedback Available"}
                                    </Typography>
                                    <Typography variant="body1" className={Styles.noFeedbackText}>
                                        {notAttempted
                                            ? "User has not submitted any response to this case study."
                                            : "There is no feedback data available for this item yet."
                                        }
                                    </Typography>
                                </Paper>
                            </div>
                        ) : (
                            <div className={Styles.responseSection}>
                                {/* Classification Indicator */}
                                <Alert severity={getClassification(currentFeedback.classification).type} className={Styles.classificationAlert}>
                                    <Typography variant="body1">
                                        Classification: {getClassification(currentFeedback.classification).text}
                                    </Typography>
                                </Alert>
                                {/* Case Study Scenario */}
                                <Paper elevation={2} className={Styles.scenarioPaper}>
                                    <Typography variant="h5" className={Styles.sectionTitle}>
                                        Case Study Scenario
                                    </Typography>
                                    <Typography variant="body1" className={Styles.scenarioText}>
                                        {currentFeedback?.scenario || "No scenario information available."}
                                    </Typography>
                                </Paper>

                                {/* Case Study Question */}
                                <Paper elevation={2} className={Styles.questionPaper}>
                                    <Typography variant="h5" className={Styles.sectionTitle}>
                                        Question
                                    </Typography>
                                    <Typography variant="body1" className={Styles.questionText}>
                                        {currentFeedback?.question || "No question information available."}
                                    </Typography>
                                </Paper>

                                {/* User Response */}
                                <Paper elevation={2} className={Styles.userResponsePaper}>
                                    <Typography variant="h5" className={Styles.sectionTitle}>
                                        User Response
                                    </Typography>
                                    {userVideo ? (
                                        <div className={Styles.videoContainer}>
                                            <ReactPlayer
                                                ref={playerRef}
                                                className={Styles.videoPlayer}
                                                url={userVideo}
                                                controls={true}
                                            />
                                        </div>) : (
                                        <Typography variant="body1" className={Styles.responseText}>
                                            {currentFeedback?.user_response || "No response information available."}
                                        </Typography>
                                    )}
                                    {userVideo && (
                                        <div
                                            id='transcriptContainer'
                                            style={{
                                                backgroundColor: '#f2f2f2',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                marginTop: '1em',
                                                padding: '1em',
                                                borderRadius: '8px',
                                            }}
                                        >
                                            <span className={Styles.titleContainer}>
                                                <Typography variant='h6' color='text.primary'>
                                                    Video Transcript
                                                </Typography>
                                                <span className={Styles.aiGenText}>
                                                    <Typography variant='subtitle2' sx={{ display: 'flex', alignItems: 'center' }}>
                                                        < AutoAwesomeRounded
                                                            fontSize='small'
                                                            sx={{
                                                                mr: 0.5,
                                                                color: '#1450a0'
                                                            }}
                                                        /> AI-Generated
                                                    </Typography>
                                                </span>
                                            </span>
                                            <span id='transcriptComponentContainer' className={Styles.transcriptComponentContainer}>
                                                {transcript ? (
                                                    <>
                                                        <Transcript transcriptData={transcript} playerRef={playerRef} />
                                                        <Typography variant='caption' color='text.secondary' sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <Info fontSize='small' sx={{ marginRight: 0.5 }} /> Poor audio quality may result in an incomplete or inaccurate transcript.
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <Typography variant='body2' color='text.secondary' sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Info fontSize='small' sx={{ mr: 0.5 }} /> No transcript is available for the current video yet.
                                                    </Typography>
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </Paper>
                                {!!currentFeedback?.is_required && (
                                    <Typography variant="body2" color="textSecondary" className={Styles.footerText}>
                                        This was a required question
                                    </Typography>
                                )}
                            </div>
                        )}
                    </>

                )
            }

            {/*Footer Section */}
            <div className={`${Styles.footerSection} ${Styles.hideForPdf}`}>
                <Info className={Styles.feedbackIcon} />
                <Typography variant="body2" color="textSecondary" className={Styles.footerText}>
                    This evaluation is based on AI analysis of the case study criteria and may not be perfect
                </Typography>
            </div>
        </div >
    );
}